// "Sync my contacts" — reads the SIGNED-IN USER'S OWN Outlook contacts and
// email signatures, diffs them against All Contacts, and proposes changes.
// Same delegated-Graph pattern as directorySync.ts and emailSync.ts: the user
// only ever reads their own mailbox, and nothing here writes — the review
// modal decides what gets applied.
//
// Built 2026-08-07 (Chauncey). This is the in-app version of the 2026-07-15
// crm-contact-audit skill; the audit found 720 of 800 CRM contacts carry no
// company at all, so most of the value is in what mailboxes know and the CRM
// doesn't.
//
// PRIVACY RULE, carried over from that skill and enforced HERE, client-side:
// only the SIGNATURE BLOCK of a message ever leaves the browser. Bodies are
// trimmed to their trailing signature lines before anything is sent to the
// API for parsing — quoted history, thread content, and the substance of the
// message are dropped in the browser and never transmitted or stored.

import { msalInstance } from './msalConfig'
import { signedInAccount } from './ssoMemory'
import type { Contact } from '@/types/entities'

// Requested SEPARATELY on purpose. Asking for both at once means a tenant
// that has consented to only one gets a token for neither — which would take
// signature harvesting down along with the Outlook-contacts read. As of
// 2026-08-07 Mail.Read is consented on all three app regs and Contacts.Read
// is not, so the contacts half degrades on its own until that grant lands.
const CONTACTS_SCOPES = ['Contacts.Read']
const MAIL_SCOPES = ['Mail.Read']
const LOOKBACK_MONTHS = 12
const PAGE_SIZE = 50
const MAX_MESSAGE_PAGES = 6 // ≤300 recent messages scanned
const MAX_SENDERS = 200 // signature blocks sent for parsing, newest first
const SIGNATURE_MAX_LINES = 14
const SIGNATURE_MAX_CHARS = 700

// Both WPI mail domains, excluded outright (Chauncey 2026-08-26). Internal
// staff are owned by the Entra directory sync — a colleague must never be
// created, or edited, by a mailbox scan. Used on the SENDER address and,
// separately, on the signature text itself (see extractSignature).
const INTERNAL_DOMAINS = ['westportproperties.net', 'usstorage.net'] as const
const INTERNAL_ADDRESS_RE = /[^\s@]+@(?:westportproperties\.net|usstorage\.net)\b/i

export function isInternalEmail(email: string): boolean {
  const e = (email ?? '').trim().toLowerCase()
  return INTERNAL_DOMAINS.some((d) => e.endsWith(`@${d}`))
}

export interface OutlookContact {
  displayName: string | null
  givenName: string | null
  surname: string | null
  companyName: string | null
  jobTitle: string | null
  department: string | null
  emailAddresses: { address?: string; name?: string }[]
  businessPhones: string[]
  mobilePhone: string | null
}

/** One person's signature text, ready for parsing. Body content is already
 *  stripped — `block` is signature lines only. */
export interface SignatureCandidate {
  email: string
  displayName: string
  block: string
  sentAt: string
}

/** What the parser gives back for one signature. */
export interface ParsedSignature {
  email: string
  firstName: string
  lastName: string
  title: string
  company: string
  officePhone: string
  cellPhone: string
}

async function graphToken(scopes: string[]): Promise<string> {
  // The ACTIVE account — the link's person, or whoever just signed in (ssoMemory.ts).
  const account = signedInAccount(msalInstance, msalInstance.getAllAccounts())
  if (!account) throw new Error('No signed-in account')
  try {
    return (await msalInstance.acquireTokenSilent({ account, scopes })).accessToken
  } catch {
    // First consent for a fresh scope (or a silent failure) — popup keeps the
    // page alive. Same fallback as directorySync/emailSync; silent-only would
    // fail forever on a newly-added scope (the 2026-07-13 lesson).
    return (await msalInstance.acquireTokenPopup({ account, scopes })).accessToken
  }
}

async function graphGet<T>(url: string, token: string, extraHeaders: Record<string, string> = {}): Promise<T> {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}`, ...extraHeaders } })
  if (res.status === 403) {
    throw new Error('Access was declined — approve the Outlook contacts/mail prompt and retry.')
  }
  if (!res.ok) throw new Error(`Graph read failed (${res.status})`)
  return (await res.json()) as T
}

// ---- Outlook contacts -------------------------------------------------------

/**
 * The user's Outlook contacts folder.
 *
 * Returns `null` — rather than throwing — when Contacts.Read isn't available,
 * so a tenant without that consent still gets the signature half of the sync
 * instead of an error screen. The caller surfaces the degraded state.
 */
export async function fetchOutlookContacts(): Promise<OutlookContact[] | null> {
  let token: string
  try {
    token = await graphToken(CONTACTS_SCOPES)
  } catch {
    return null // consent not granted / declined — carry on with signatures
  }
  const select = 'displayName,givenName,surname,companyName,jobTitle,department,emailAddresses,businessPhones,mobilePhone'
  let url: string | null = `https://graph.microsoft.com/v1.0/me/contacts?$select=${select}&$top=${PAGE_SIZE}`
  const out: OutlookContact[] = []
  let pages = 0
  try {
    while (url && pages < 20) {
      const body: { value: OutlookContact[]; '@odata.nextLink'?: string } = await graphGet(url, token)
      out.push(...body.value)
      url = body['@odata.nextLink'] ?? null
      pages += 1
    }
  } catch {
    return null
  }
  return out
}

// ---- Signature harvesting ---------------------------------------------------

interface GraphMessage {
  id: string
  from?: { emailAddress: { name?: string; address?: string } }
  sentDateTime: string
  body?: { content?: string }
}

/**
 * Reduce a plain-text mail body to its signature block.
 *
 * This is the privacy boundary: everything above the signature is discarded
 * in the browser. Quoted history is cut at the first reply marker, then the
 * trailing lines are kept — that tail is what carries name/title/company/phone.
 */
export function extractSignature(body: string): string {
  if (!body) return ''
  let text = body.replace(/\r\n/g, '\n')

  // Cut quoted history: everything from the first reply marker onward.
  const replyMarkers = [
    /^\s*-{2,}\s*Original Message\s*-{2,}/im,
    /^\s*From:\s.+$/im,
    /^\s*On .{4,80}\bwrote:\s*$/im,
    /^\s*_{5,}\s*$/m,
  ]
  for (const re of replyMarkers) {
    const m = re.exec(text)
    if (m && m.index > 0) text = text.slice(0, m.index)
  }

  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean)
  if (!lines.length) return ''

  // The FIRST sign-off, not the last.
  //
  // This used to be `.reverse().find(...)`, i.e. the LAST sign-off in the
  // text. When the quoted-history cut above misses — a format we don't
  // recognise, HTML flattened to text, a forward inside a forward — the last
  // sign-off is the OLDEST message's, so the block returned belonged to
  // whoever wrote the bottom of the chain rather than to the sender. That is
  // how an internal colleague's title and direct line reached an external
  // contact's card on 2026-08-26. The first sign-off is the newest message's
  // in a thread, and the only one in a clean message.
  const delimIdx = lines.findIndex((l) =>
    /^(--|__|—)\s*$/.test(l)
    || /^(regards|best|thanks|sincerely|cheers|best regards|kind regards)[,.!]?$/i.test(l))

  let tail: string[]
  if (delimIdx >= 0) {
    tail = lines.slice(delimIdx + 1)
  } else if (lines.length <= SIGNATURE_MAX_LINES * 2) {
    // Short note, no sign-off: the trailing lines really are the block.
    tail = lines.slice(-SIGNATURE_MAX_LINES)
  } else {
    // Long text, no sign-off, and no reply marker we recognised — we cannot
    // say which lines are the SENDER'S signature. Taking the last 14 lines of
    // a thread is precisely how a stranger's details get attributed to them.
    // Skip this sender: no candidate beats a wrong one.
    return ''
  }

  const block = tail.slice(0, SIGNATURE_MAX_LINES).join('\n').slice(0, SIGNATURE_MAX_CHARS)
  // A block carrying an internal address is a colleague's signature that rode
  // along inside someone else's mail. Never offer it as the sender's.
  // (The API enforces this again — see routes/contact-sync.ts.)
  return INTERNAL_ADDRESS_RE.test(block) ? '' : block
}

function sinceIsoDate(): string {
  const d = new Date()
  d.setMonth(d.getMonth() - LOOKBACK_MONTHS)
  return d.toISOString().slice(0, 10)
}

/**
 * One signature per external sender — the most recent message from each.
 * Scanning per-sender rather than per-message is what keeps this tractable:
 * a few hundred messages collapse to at most MAX_SENDERS parse candidates.
 */
export async function fetchSignatureCandidates(): Promise<SignatureCandidate[]> {
  const token = await graphToken(MAIL_SCOPES)
  const since = sinceIsoDate()
  const select = 'id,from,sentDateTime,body'
  let url: string | null =
    `https://graph.microsoft.com/v1.0/me/messages?$filter=${encodeURIComponent(`receivedDateTime ge ${since}`)}` +
    `&$select=${select}&$orderby=receivedDateTime desc&$top=${PAGE_SIZE}`

  const newestBySender = new Map<string, SignatureCandidate>()
  let pages = 0
  while (url && pages < MAX_MESSAGE_PAGES) {
    const body: { value: GraphMessage[]; '@odata.nextLink'?: string } = await graphGet(
      url, token, { Prefer: 'outlook.body-content-type="text"' },
    )
    for (const m of body.value) {
      const email = (m.from?.emailAddress.address ?? '').trim().toLowerCase()
      if (!email || isInternalEmail(email)) continue // internal staff come from the directory sync
      if (newestBySender.has(email)) continue // ordered desc — first hit is newest
      const block = extractSignature(m.body?.content ?? '')
      if (!block) continue
      newestBySender.set(email, {
        email,
        displayName: m.from?.emailAddress.name ?? '',
        block,
        sentAt: m.sentDateTime,
      })
    }
    url = body['@odata.nextLink'] ?? null
    pages += 1
  }

  return [...newestBySender.values()]
    .sort((a, b) => b.sentAt.localeCompare(a.sentAt))
    .slice(0, MAX_SENDERS)
}

// ---- Planning ---------------------------------------------------------------

/** A single reviewable proposal. One row, one Yes/No. */
export interface ContactProposal {
  key: string
  kind: 'create' | 'fill' | 'conflict'
  contactId?: string
  /** Display name for the row. */
  name: string
  email: string
  /** Where this came from, shown so the reviewer can judge it. */
  source: 'Outlook contacts' | 'Email signature'
  /** Field-level changes: field, current value (conflicts only), proposed. */
  changes: { field: keyof Contact; label: string; from?: string; to: string }[]
  /** Ready-to-apply patch (updates) or full record (creates). */
  patch: Partial<Contact>
  create?: Contact
}

const FIELD_LABELS: Partial<Record<keyof Contact, string>> = {
  title: 'Title',
  department: 'Department',
  workEmail: 'Work email',
  officePhone: 'Office phone',
  cellPhone: 'Mobile',
  firstName: 'First name',
  lastName: 'Last name',
}

const clean = (v: string | null | undefined): string => (v ?? '').trim()

function slugId(email: string): string {
  const alias = email.split('@')[0].toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  return `con-sync-${alias || 'contact'}`
}

/** Candidate field values from either source, normalised to one shape. */
interface Candidate {
  email: string
  firstName: string
  lastName: string
  title: string
  officePhone: string
  cellPhone: string
  company: string
  source: ContactProposal['source']
}

function fromOutlook(c: OutlookContact): Candidate | null {
  const email = clean(c.emailAddresses?.[0]?.address).toLowerCase()
  if (!email || isInternalEmail(email)) return null
  const display = clean(c.displayName)
  return {
    email,
    firstName: clean(c.givenName) || display.split(' ')[0] || '',
    lastName: clean(c.surname) || display.split(' ').slice(1).join(' '),
    title: clean(c.jobTitle),
    officePhone: clean(c.businessPhones?.[0]),
    cellPhone: clean(c.mobilePhone),
    company: clean(c.companyName),
    source: 'Outlook contacts',
  }
}

function fromSignature(s: ParsedSignature): Candidate | null {
  const email = clean(s.email).toLowerCase()
  if (!email || isInternalEmail(email)) return null
  return {
    email,
    firstName: clean(s.firstName),
    lastName: clean(s.lastName),
    title: clean(s.title),
    officePhone: clean(s.officePhone),
    cellPhone: clean(s.cellPhone),
    company: clean(s.company),
    source: 'Email signature',
  }
}

/**
 * Diff candidates against the CRM. Pure — the caller applies.
 *
 * Three proposal kinds, kept apart because they carry different risk:
 *   create   — not in the CRM at all
 *   fill     — the CRM field is empty; adding is safe
 *   conflict — the CRM has a DIFFERENT value; shown old → new so a job change
 *              is noticed rather than rubber-stamped (Chauncey 2026-08-07)
 *
 * `company` is deliberately NOT written: Contact has no company field — it's
 * accountId resolved to the account's name at serve time, so a "company" edit
 * is a link or a rename, not a field write. It is surfaced on create rows as
 * context only.
 */
export function planContactSync(
  outlook: OutlookContact[],
  signatures: ParsedSignature[],
  contacts: Contact[],
): ContactProposal[] {
  const byEmail = new Map<string, Contact>()
  // WPI staff are indexed too — not to be edited, but so a candidate that
  // resolves to a colleague can be recognised and dropped below rather than
  // creating a second card for them. A staff member with a personal address
  // on their card is otherwise reachable from a mailbox scan.
  for (const c of contacts) {
    if (c.archived) continue
    for (const e of [c.workEmail, c.personalEmail]) {
      const k = clean(e).toLowerCase()
      if (k && !byEmail.has(k)) byEmail.set(k, c)
    }
  }

  // Outlook contacts are structured data and win over parsed signature text.
  const candidates = new Map<string, Candidate>()
  for (const s of signatures) {
    const c = fromSignature(s)
    if (c) candidates.set(c.email, c)
  }
  for (const o of outlook) {
    const c = fromOutlook(o)
    if (c) candidates.set(c.email, c)
  }

  const out: ContactProposal[] = []
  for (const cand of candidates.values()) {
    const existing = byEmail.get(cand.email)
    // Internal people are the directory sync's job, end to end. This covers
    // the case the address filter cannot: a colleague whose CRM card carries
    // a personal (external) address.
    if (existing?.isWpi || existing?.type === 'WPI Employee') continue

    if (!existing) {
      if (!cand.firstName && !cand.lastName) continue // nothing to name the card
      const id = slugId(cand.email)
      const create: Contact = {
        id,
        firstName: cand.firstName || cand.email.split('@')[0],
        lastName: cand.lastName,
        workEmail: cand.email,
        trackMemberships: [], // REQUIRED by the prototype — see the 2026-07-14 white-screen incident
        ...(cand.title ? { title: cand.title } : {}),
        ...(cand.officePhone ? { officePhone: cand.officePhone } : {}),
        ...(cand.cellPhone ? { cellPhone: cand.cellPhone } : {}),
      }
      const changes: ContactProposal['changes'] = [
        { field: 'workEmail', label: 'Work email', to: cand.email },
      ]
      if (cand.title) changes.push({ field: 'title', label: 'Title', to: cand.title })
      if (cand.officePhone) changes.push({ field: 'officePhone', label: 'Office phone', to: cand.officePhone })
      if (cand.cellPhone) changes.push({ field: 'cellPhone', label: 'Mobile', to: cand.cellPhone })
      out.push({
        key: `create:${cand.email}`,
        kind: 'create',
        name: `${cand.firstName} ${cand.lastName}`.trim(),
        email: cand.email,
        source: cand.source,
        // Company is context only — it is not written (see the doc comment).
        changes: cand.company ? [...changes, { field: 'department', label: 'Company (not applied)', to: cand.company }] : changes,
        patch: {},
        create,
      })
      continue
    }

    const fills: ContactProposal['changes'] = []
    const conflicts: ContactProposal['changes'] = []
    const fillPatch: Partial<Contact> = {}
    const conflictPatch: Partial<Contact> = {}

    const compare = (field: 'title' | 'officePhone' | 'cellPhone', value: string) => {
      if (!value) return
      const current = clean(existing[field] as string | undefined)
      const label = FIELD_LABELS[field] ?? field
      if (!current) {
        fills.push({ field, label, to: value })
        ;(fillPatch as Record<string, unknown>)[field] = value
      } else if (current.toLowerCase() !== value.toLowerCase()) {
        conflicts.push({ field, label, from: current, to: value })
        ;(conflictPatch as Record<string, unknown>)[field] = value
      }
    }
    /** Names are FILL-ONLY. A blank surname is a hole worth closing — the
     *  sync used to skip names entirely for an existing contact, so a card
     *  imported without one stayed nameless no matter how many signatures
     *  carried it (Robert Masterson's run, 2026-08-26: "it didn't even pull
     *  her last name"). Overwriting a name that IS set is a different act:
     *  "Bob" vs "Robert" is not a correction, and renaming a person off a
     *  parsed signature is not a change this sync should ever propose. */
    const fillName = (field: 'firstName' | 'lastName', value: string) => {
      if (!value) return
      if (clean(existing[field] as string | undefined)) return
      fills.push({ field, label: FIELD_LABELS[field] ?? field, to: value })
      ;(fillPatch as Record<string, unknown>)[field] = value
    }
    compare('title', cand.title)
    compare('officePhone', cand.officePhone)
    compare('cellPhone', cand.cellPhone)
    fillName('firstName', cand.firstName)
    fillName('lastName', cand.lastName)

    const name = `${existing.firstName} ${existing.lastName}`.trim()
    if (fills.length) {
      out.push({
        key: `fill:${existing.id}`, kind: 'fill', contactId: existing.id,
        name, email: cand.email, source: cand.source, changes: fills, patch: fillPatch,
      })
    }
    if (conflicts.length) {
      out.push({
        key: `conflict:${existing.id}`, kind: 'conflict', contactId: existing.id,
        name, email: cand.email, source: cand.source, changes: conflicts, patch: conflictPatch,
      })
    }
  }

  // Creates first (most value), then safe fills, then the ones needing judgement.
  const order = { create: 0, fill: 1, conflict: 2 }
  return out.sort((a, b) => order[a.kind] - order[b.kind] || a.name.localeCompare(b.name))
}
